import {
  backButton,
  closingBehavior,
  init,
  isTMA,
  miniApp,
  retrieveLaunchParams,
  retrieveRawInitData,
  swipeBehavior,
  themeParams,
  viewport,
} from '@tma.js/sdk-react';

/** Call a Telegram feature if this client supports it; never crash the app over chrome. */
function attempt(fn: () => unknown): void {
  try {
    const r = fn();
    if (r instanceof Promise) r.catch(() => undefined);
  } catch {
    /* unsupported in this Telegram version */
  }
}

export interface Launch {
  initData: string;
  userId: number | null;
  startParam: string | null;
}

/** Initialise the Telegram Mini Apps SDK (theme, viewport, back button). Null outside Telegram. */
export function boot(): Launch | null {
  if (!isTMA()) return null;
  attempt(() => init());
  attempt(() => miniApp.mount());
  attempt(() => themeParams.mount());
  attempt(() => miniApp.bindCssVars());
  attempt(() => themeParams.bindCssVars());
  attempt(() => backButton.mount());
  attempt(() => closingBehavior.mount());
  attempt(() => swipeBehavior.mount());
  attempt(() => viewport.mount());
  attempt(() => viewport.expand());
  applyTheme();
  attempt(() => miniApp.ready());
  const initData = retrieveRawInitData() ?? '';
  let userId: number | null = null;
  let startParam: string | null = null;
  try {
    const lp = retrieveLaunchParams();
    userId = lp.tgWebAppData?.user?.id ?? null;
    startParam = lp.tgWebAppStartParam ?? null;
  } catch {
    // Outside Telegram (or with malformed params) there are no launch params; the user id and
    // start param are only hints, and the API's init-data check decides who the user is.
  }
  return initData ? { initData, userId, startParam } : null;
}

/**
 * Brand theme that follows Telegram's light/dark choice: <html data-theme> picks the brand token
 * set, and Telegram's header/background are painted to match so the app feels native.
 */
function applyTheme(): void {
  const paint = (dark: boolean) => {
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
    const bg = dark ? '#0d3340' : '#f4fafc';
    attempt(() => miniApp.setHeaderColor(bg));
    attempt(() => miniApp.setBgColor(bg));
  };
  attempt(() => {
    paint(miniApp.isDark());
    miniApp.isDark.sub(paint);
  });
}

/** During a mock: ask before closing and stop swipe-down from closing the app (spec §3.4). */
export function guardMock(on: boolean): void {
  if (on) {
    attempt(() => closingBehavior.enableConfirmation());
    attempt(() => swipeBehavior.disableVertical());
  } else {
    attempt(() => closingBehavior.disableConfirmation());
    attempt(() => swipeBehavior.enableVertical());
  }
}

/** Show Telegram's back button wired to `onBack`; returns a cleanup. */
export function bindBack(onBack: (() => void) | null): () => void {
  if (!onBack) {
    attempt(() => backButton.hide());
    return () => undefined;
  }
  attempt(() => backButton.show());
  let off: VoidFunction = () => undefined;
  attempt(() => {
    off = backButton.onClick(onBack);
  });
  return () => {
    off();
    attempt(() => backButton.hide());
  };
}

/** Close the Mini App and return to the bot chat (e.g. to buy with /upgrade). */
export function closeApp(): void {
  attempt(() => miniApp.close());
}
